<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import WootSubmitButton from 'dashboard/components/buttons/FormSubmitButton.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const phoneNumber = ref('');
const inbox = ref('');

const rules = {
  inbox: { required },
  name: { required },
  phoneNumber: { required},
};

const v$ = useVuelidate(rules, {
  inbox,
  name,
  phoneNumber,
});

const mapToOptions = (items, valueKey, labelKey) =>
    items?.map(item => ({
      value: item[valueKey],
      label: item[labelKey],
    })) ?? [];

const uiFlags = useMapGetter('whatsappAccounts/getUIFlags');
const inboxes = useMapGetter('inboxes/getWhatsappPersonalInboxes');
const inboxOptions = computed(() =>
    mapToOptions(inboxes.value, 'id', 'name')
);

const addWhatsappAccount = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      inbox: inbox.value,
      name: name.value,
      phone_number: phoneNumber.value,
    };

    await store.dispatch('whatsappAccounts/createWhatsappAccount', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title=" 'Add Whatsapp Account' "
      :header-content=" '' "
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="false">

      <div class="w-full">
        <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.LABEL') }}
        </label>
        <ComboBox
            id="inbox"
            v-model="inbox"
            :options="inboxOptions"
            :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.PLACEHOLDER')"
            class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>

      <div class="w-full">
        <label :class="{ error: v$.phoneNumber.$error }">
          Phone Number
          <PhoneNumberInput
              v-model="phoneNumber"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          Name
          <input
            v-model="name"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.name.$touch"
          />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <div class="w-full">
          <WootSubmitButton
            :disabled="v$.$invalid || uiFlags.isCreating"
            :button-text="'Add Account'"
            :loading="uiFlags.isCreating"
            @click="addWhatsappAccount"
          />
          <button class="button clear" @click.prevent="emit('close')">
            {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
